import React, { useState, useMemo } from "react";
import { CiSettings } from "react-icons/ci";
import {
  FaBolt,
  FaSlidersH,
  FaSitemap,
  FaMicrochip,
  FaSearch,
  FaTimes,
  FaCheckCircle,
} from "react-icons/fa";
import { formatDisplayName } from "../../../utils/textUtils";

const glassPanel = {
  background: "rgba(13,21,38,0.78)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow:
    "0 12px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
};

interface SystemListProps {
  selectedSite: any;
  systems: any[];
  selectedSystemId: string | null;
  setSelectedSystemId: (id: string | null) => void;
  onEditCriticalValue?: (site: any) => void;
  onSelectSystemCallback?: (system: any) => void;
}

const SystemList: React.FC<SystemListProps> = ({
  selectedSite,
  systems,
  selectedSystemId,
  setSelectedSystemId,
  onEditCriticalValue,
  onSelectSystemCallback,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const threshold =
    selectedSite?.criticalResistanceValue != null
      ? Number(selectedSite.criticalResistanceValue).toFixed(1)
      : "3.0";

  const totalPitsInSite = useMemo(() => {
    return systems.reduce(
      (acc: number, sys: any) => acc + (sys.sensors?.length || 0),
      0
    );
  }, [systems]);

  const filteredSystems = useMemo(() => {
    if (!searchQuery.trim()) return systems;
    const q = searchQuery.toLowerCase().trim();
    return systems.filter((sys: any) => {
      const name = (sys.name || "").toLowerCase();
      const desc = (sys.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [systems, searchQuery]);

  return (
    <main
      className="flex-1 rounded-2xl overflow-hidden flex flex-col min-h-[420px] xl:min-h-0 xl:h-full transition-all duration-300"
      style={glassPanel}
    >
      {/* Header */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(99,102,241,0.15) 100%)",
              border: "1px solid rgba(168,85,247,0.3)",
              boxShadow: "0 0 16px rgba(168,85,247,0.2)",
            }}
          >
            <CiSettings className="w-5 h-5 text-purple-400 stroke-1" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm sm:text-base font-bold text-white tracking-tight">
                Systems
              </h2>
              {selectedSite && (
                <span className="text-xs text-purple-300 font-semibold truncate max-w-[200px] sm:max-w-xs">
                  — {formatDisplayName(selectedSite.name)}
                </span>
              )}
            </div>
            <p className="text-[10px] text-slate-400 font-medium">
              Sub-grid grounding distribution systems
            </p>
          </div>
        </div>

        {selectedSite && (
          <div className="flex items-center gap-2 flex-wrap">
            {selectedSite.siteExternalId && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-slate-800/90 text-slate-300 border border-slate-700/80 font-mono">
                EXT: #{selectedSite.siteExternalId}
              </span>
            )}
            <span
              className="text-xs font-semibold px-2.5 py-1 rounded-full border"
              style={{
                background: "rgba(168,85,247,0.10)",
                border: "1px solid rgba(168,85,247,0.25)",
                color: "#c084fc",
              }}
            >
              {systems.length} {systems.length === 1 ? "system" : "systems"}
            </span>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-5 custom-scrollbar space-y-4 sm:space-y-5 min-h-0">
        {/* Selected Site Overview & Threshold Card */}
        {selectedSite && (
          <div
            className="p-4 sm:p-5 rounded-2xl relative overflow-hidden transition-all duration-300"
            style={{
              background:
                "linear-gradient(135deg, rgba(20, 30, 60, 0.75) 0%, rgba(10, 16, 35, 0.85) 100%)",
              border: "1px solid rgba(59, 130, 246, 0.22)",
              boxShadow: "0 8px 30px rgba(0, 0, 0, 0.35)",
            }}
          >
            {/* Ambient backlight glow */}
            <div
              className="absolute -right-12 -top-12 w-48 h-48 rounded-full pointer-events-none opacity-15 blur-3xl"
              style={{ background: "#3b82f6" }}
            />
            <div
              className="absolute -left-12 -bottom-12 w-48 h-48 rounded-full pointer-events-none opacity-10 blur-3xl"
              style={{ background: "#f59e0b" }}
            />

            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Site Info */}
              <div className="flex items-start sm:items-center gap-3.5 min-w-0 flex-1">
                <div
                  className="p-3 rounded-2xl flex items-center justify-center text-blue-400 shadow-md flex-shrink-0"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(99, 102, 241, 0.12) 100%)",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                  }}
                >
                  <FaSitemap className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base sm:text-lg font-bold text-white truncate">
                      {formatDisplayName(selectedSite.name)}
                    </h3>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 line-clamp-2 max-w-xl">
                    {selectedSite.description ||
                      "Monitoring site with linked grounding distribution networks."}
                  </p>

                  {/* Summary pills */}
                  <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                    <span className="text-[11px] px-2.5 py-0.5 rounded-lg bg-blue-500/10 text-blue-300 border border-blue-500/20 font-medium flex items-center gap-1.5">
                      <CiSettings className="w-3.5 h-3.5" />
                      {systems.length} Systems
                    </span>
                    <span className="text-[11px] px-2.5 py-0.5 rounded-lg bg-purple-500/10 text-purple-300 border border-purple-500/20 font-medium flex items-center gap-1.5">
                      <FaMicrochip className="w-3 h-3 text-purple-400" />
                      {totalPitsInSite} Earth Pits
                    </span>
                  </div>
                </div>
              </div>

              {/* Critical Threshold Badge & Edit Button */}
              <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap flex-shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-white/[0.06]">
                <div
                  className="px-3.5 py-2 rounded-2xl flex items-center gap-2.5 border"
                  style={{
                    background: "rgba(245, 158, 11, 0.10)",
                    borderColor: "rgba(245, 158, 11, 0.30)",
                    boxShadow: "0 0 15px rgba(245,158,11,0.12)",
                  }}
                >
                  <FaBolt className="w-4 h-4 text-amber-400 animate-pulse flex-shrink-0" />
                  <div>
                    <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Critical Threshold
                    </div>
                    <div className="text-sm sm:text-base font-extrabold font-mono text-amber-300">
                      {threshold} Ω
                    </div>
                  </div>
                </div>

                {onEditCriticalValue && (
                  <button
                    type="button"
                    onClick={() => onEditCriticalValue(selectedSite)}
                    className="px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-2xl text-xs font-bold text-white flex items-center gap-2 transition-all cursor-pointer active:scale-95 shadow-lg group flex-shrink-0"
                    style={{
                      background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                      boxShadow: "0 4px 16px rgba(37, 99, 235, 0.35)",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                    }}
                    title="Set or update the critical resistance value for this site"
                  >
                    <FaSlidersH className="w-3.5 h-3.5 group-hover:rotate-45 transition-transform duration-200" />
                    <span>Set Critical Value</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Search within systems if > 2 systems */}
        {systems.length > 2 && (
          <div className="relative flex items-center">
            <FaSearch className="w-3.5 h-3.5 text-slate-400 absolute left-3 pointer-events-none" />
            <input
              type="text"
              placeholder="Filter systems by name or description…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-8 py-2 text-xs rounded-xl text-white placeholder-slate-500 transition-all outline-none"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.07)",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "rgba(168,85,247,0.5)";
                e.target.style.boxShadow = "0 0 0 2px rgba(168,85,247,0.15)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "rgba(255,255,255,0.07)";
                e.target.style.boxShadow = "none";
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 p-1 text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                <FaTimes className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        )}

        {/* Empty States */}
        {!selectedSite ? (
          <div className="py-24 flex flex-col items-center justify-center text-slate-500 text-center px-4 gap-3">
            <div
              className="p-4 rounded-2xl"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <CiSettings className="w-12 h-12 text-blue-400 opacity-40 stroke-1" />
            </div>
            <p className="text-sm font-semibold text-slate-300">
              No Site Selected
            </p>
            <p className="text-xs text-slate-500 max-w-xs">
              Select a facility site from the left panel to explore connected
              grounding systems.
            </p>
          </div>
        ) : systems.length === 0 ? (
          <div
            className="py-16 flex flex-col items-center justify-center rounded-2xl text-slate-500 text-center px-6 gap-3"
            style={{
              background: "rgba(0,0,0,0.2)",
              border: "1px dashed rgba(255,255,255,0.08)",
            }}
          >
            <CiSettings className="w-10 h-10 text-slate-600 opacity-50 stroke-1" />
            <p className="text-sm font-semibold text-slate-400">
              No Systems Connected
            </p>
            <p className="text-xs text-slate-600 max-w-sm">
              This site currently does not have any sub-systems configured.
            </p>
          </div>
        ) : filteredSystems.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-xs">
            No systems found matching "{searchQuery}".
          </div>
        ) : (
          /* Systems Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-3 gap-3.5 sm:gap-4">
            {filteredSystems.map((system: any) => {
              const active = system.id === selectedSystemId;
              const pitCount = system.sensors?.length || 0;

              return (
                <div
                  key={system.id}
                  onClick={() => {
                    setSelectedSystemId(system.id);
                    if (onSelectSystemCallback) onSelectSystemCallback(system);
                  }}
                  className={`cursor-pointer group relative rounded-2xl p-4 sm:p-5 transition-all duration-300 overflow-hidden ${
                    active ? "hover:-translate-y-0.5" : "hover:-translate-y-1"
                  }`}
                  style={{
                    background: active
                      ? "linear-gradient(145deg, rgba(59,130,246,0.18) 0%, rgba(13,21,38,0.85) 100%)"
                      : "linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)",
                    border: active
                      ? "1px solid rgba(59,130,246,0.4)"
                      : "1px solid rgba(255,255,255,0.06)",
                    boxShadow: active
                      ? "0 10px 30px rgba(59,130,246,0.18), inset 0 1px 0 rgba(255,255,255,0.1)"
                      : "0 4px 20px rgba(0,0,0,0.25)",
                  }}
                >
                  {/* Subtle dot matrix background */}
                  <div
                    className="absolute inset-0 opacity-[0.03] pointer-events-none"
                    style={{
                      backgroundImage:
                        "radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)",
                      backgroundSize: "18px 18px",
                    }}
                  />

                  {/* Top active highlight glow */}
                  {active && (
                    <div
                      className="absolute top-0 left-0 right-0 h-1 rounded-t-full"
                      style={{
                        background:
                          "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)",
                        boxShadow: "0 0 10px rgba(59,130,246,0.8)",
                      }}
                    />
                  )}

                  <div className="relative z-10 flex flex-col h-full justify-between gap-3">
                    {/* Header info */}
                    <div>
                      <div className="flex items-start gap-3 mb-2.5">
                        <div
                          className={`p-2.5 rounded-xl flex-shrink-0 transition-all ${
                            active
                              ? "bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-[0_0_15px_rgba(59,130,246,0.35)]"
                              : "text-slate-400 group-hover:text-blue-400 bg-white/[0.05] group-hover:bg-white/[0.08]"
                          }`}
                        >
                          <CiSettings className="w-5 h-5 stroke-1" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className={`text-sm font-bold truncate transition-colors ${
                              active
                                ? "text-white"
                                : "text-slate-200 group-hover:text-white"
                            }`}
                          >
                            {formatDisplayName(system.name)}
                          </p>
                          <p
                            className={`text-xs mt-0.5 line-clamp-2 leading-relaxed ${
                              active ? "text-blue-200/80" : "text-slate-400"
                            }`}
                          >
                            {system.description ||
                              "Sub-distribution earth pit monitoring loop"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Card Footer: status indicator & pit count badge */}
                    <div
                      className="flex items-center justify-between pt-3 gap-2"
                      style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                    >
                      <div className="flex items-center gap-2">
                        {active ? (
                          <div className="flex items-center gap-1.5 text-blue-300">
                            <FaCheckCircle className="w-3.5 h-3.5 text-blue-400 shadow-sm" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">
                              Selected
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-slate-500 group-hover:text-slate-300 transition-colors">
                            <div className="w-2 h-2 rounded-full bg-slate-600 group-hover:bg-blue-400 transition-colors" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">
                              View Pits
                            </span>
                          </div>
                        )}
                      </div>

                      <span
                        className={`text-xs px-2.5 py-0.5 rounded-full border font-semibold flex items-center gap-1.5 transition-colors ${
                          active
                            ? "bg-blue-500/15 text-blue-300 border-blue-500/30"
                            : "bg-slate-800/80 text-slate-400 border-slate-700/60 group-hover:border-slate-600"
                        }`}
                      >
                        <FaMicrochip className="w-2.5 h-2.5 opacity-70" />
                        {pitCount} {pitCount === 1 ? "pit" : "pits"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

export default React.memo(SystemList);
